import React, { useState, useEffect, useCallback } from 'react';
import MainLayout from '../components/MainLayout';
import axios from 'axios';
import { Plus, Edit, Trash2, ChevronDown, ChevronRight, Shield, Zap, Download } from 'lucide-react';
import { toast } from 'sonner';
import { downloadCSV } from '../lib/exportUtils';
import { formatApiError } from '../lib/errorUtils';
import { useActiveCompany } from '../hooks/useActiveCompany';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const RULE_TYPES = [
  { value: 'pf', label: 'Provident Fund' },
  { value: 'eps', label: 'EPS' },
  { value: 'edli', label: 'EDLI' },
  { value: 'esi', label: 'ESI' },
  { value: 'pt', label: 'Professional Tax' },
  { value: 'lwf', label: 'Labour Welfare Fund' },
  { value: 'tds', label: 'TDS' },
  { value: 'gratuity', label: 'Gratuity' },
  { value: 'bonus', label: 'Bonus' },
  { value: 'leave_encashment', label: 'Leave Encashment' },
  { value: 'notice_pay', label: 'Notice Pay' },
];

const StatutorySetup = () => {
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useActiveCompany();
  const [parameters, setParameters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editParam, setEditParam] = useState(null);
  const [filterType, setFilterType] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [form, setForm] = useState({
    rule_name: '', rule_type: 'pf', description: '', effective_from: new Date().toISOString().split('T')[0],
    effective_to: '', state: '', wage_basis: 'basic',
    employee_share: { rate: 0, basis: 'basic' }, employer_share: { rate: 0, basis: 'basic' },
    thresholds: { wage_ceiling: null }, slab_table: [], due_day: 15,
    override_permissions: { allow_voluntary_higher: false, allow_opt_out: false },
    metadata: {}, is_active: true,
  });

  const fetchCompanies = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/companies`, { withCredentials: true });
      setCompanies(data);
      setSelectedCompany((cur) => (data.length > 0 && !cur ? data[0].id : cur));
    } catch (e) { console.error(e); }
  }, [setSelectedCompany]);

  useEffect(() => { fetchCompanies(); }, [fetchCompanies]);
  const fetchParameters = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API_URL}/statutory/parameters?company_id=${selectedCompany}`;
      if (filterType) url += `&rule_type=${filterType}`;
      const { data } = await axios.get(url, { withCredentials: true });
      setParameters(data);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  }, [filterType, selectedCompany]);

  useEffect(() => { if (selectedCompany) fetchParameters(); }, [selectedCompany, filterType, fetchParameters]);



  const handleSeedDefaults = async () => {
    try {
      const { data } = await axios.post(`${API_URL}/statutory/seed-defaults?company_id=${selectedCompany}`, {}, { withCredentials: true });
      toast.success(data.message);
      fetchParameters();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      // Empty-string date inputs blow up Pydantic 2 ("input is too short") — coerce to null
      // before posting. Same applies to numeric fields with default 0.
      const payload = {
        ...form,
        company_id: selectedCompany,
        effective_to: form.effective_to || null,
        state: form.state || null,
      };
      if (editParam) {
        await axios.patch(`${API_URL}/statutory/parameters/${editParam.id}`, payload, { withCredentials: true });
        toast.success('Parameter updated');
      } else {
        await axios.post(`${API_URL}/statutory/parameters`, payload, { withCredentials: true });
        toast.success('Parameter added');
      }
      setShowModal(false);
      setEditParam(null);
      fetchParameters();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Deactivate this parameter?')) return;
    try {
      await axios.delete(`${API_URL}/statutory/parameters/${id}`, { withCredentials: true });
      toast.success('Parameter deactivated');
      fetchParameters();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const openEdit = (p) => {
    setEditParam(p);
    setForm({
      rule_name: p.rule_name || '', rule_type: p.rule_type || 'pf', description: p.description || '',
      effective_from: p.effective_from || '', effective_to: p.effective_to || '', state: p.state || '',
      wage_basis: p.wage_basis || 'basic',
      employee_share: p.employee_share || { rate: 0, basis: 'basic' },
      employer_share: p.employer_share || { rate: 0, basis: 'basic' },
      thresholds: p.thresholds || {}, slab_table: p.slab_table || [], due_day: p.due_day || 15,
      override_permissions: p.override_permissions || {},
      metadata: p.metadata || {}, is_active: p.is_active !== false,
    });
    setShowModal(true);
  };

  const typeColors = {
    pf: 'bg-blue-100 text-blue-700', eps: 'bg-blue-50 text-blue-600', edli: 'bg-indigo-100 text-indigo-700',
    esi: 'bg-emerald-100 text-emerald-700', pt: 'bg-amber-100 text-amber-700', lwf: 'bg-orange-100 text-orange-700',
    tds: 'bg-red-100 text-red-700', gratuity: 'bg-purple-100 text-purple-700', bonus: 'bg-pink-100 text-pink-700',
    leave_encashment: 'bg-teal-100 text-teal-700', notice_pay: 'bg-slate-100 text-slate-700',
  };

  return (
    <MainLayout>
      <div data-testid="statutory-setup-page" className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Statutory Setup Master</h1>
            <p className="text-sm text-slate-500 mt-1">Configure statutory rules with effective dates, rates, and applicability</p>
          </div>
          <div className="flex items-center space-x-3">
            <select data-testid="company-selector" value={selectedCompany} onChange={(e) => setSelectedCompany(e.target.value)} className="border border-slate-300 rounded-lg px-3 py-2 text-sm">
              {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <button data-testid="seed-defaults-btn" onClick={handleSeedDefaults} className="flex items-center space-x-2 border border-slate-300 text-slate-700 px-4 py-2 rounded-lg text-sm font-medium hover:bg-slate-50">
              <Zap className="w-4 h-4" /><span>Seed Defaults</span>
            </button>
            <button data-testid="add-parameter-btn" onClick={() => { setEditParam(null); setForm({ rule_name: '', rule_type: 'pf', description: '', effective_from: new Date().toISOString().split('T')[0], effective_to: '', state: '', wage_basis: 'basic', employee_share: { rate: 0, basis: 'basic' }, employer_share: { rate: 0, basis: 'basic' }, thresholds: {}, slab_table: [], due_day: 15, override_permissions: {}, metadata: {}, is_active: true }); setShowModal(true); }}
              className="flex items-center space-x-2 bg-[#0052FF] text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#0040CC]">
              <Plus className="w-4 h-4" /><span>Add Rule</span>
            </button>
            <button data-testid="statutory-export-btn" onClick={() => {
              if (parameters.length === 0) { toast.error('No parameters to export'); return; }
              downloadCSV(parameters.map(p => ({
                Rule_Name: p.rule_name, Type: p.rule_type, Effective_From: p.effective_from,
                Effective_To: p.effective_to || '', EE_Rate: p.employee_share?.rate || 0,
                ER_Rate: p.employer_share?.rate || 0, Active: p.is_active
              })), `statutory_setup_${selectedCompany}.csv`);
              toast.success('Statutory parameters exported');
            }} className="flex items-center space-x-2 bg-slate-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-slate-700">
              <Download className="w-4 h-4" /><span>Export</span>
            </button>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <button data-testid="filter-all" onClick={() => setFilterType('')} className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${!filterType ? 'bg-[#0052FF] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>All ({parameters.length})</button>
          {RULE_TYPES.map((rt) => (
            <button key={rt.value} data-testid={`filter-${rt.value}`} onClick={() => setFilterType(rt.value)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${filterType === rt.value ? 'bg-[#0052FF] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>{rt.label}</button>
          ))}
        </div>

        <div className="space-y-3">
          {parameters.map((p) => (
            <div key={p.id} data-testid={`param-card-${p.id}`} className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <div className="flex items-center justify-between p-4 cursor-pointer hover:bg-slate-50" onClick={() => setExpandedId(expandedId === p.id ? null : p.id)}>
                <div className="flex items-center space-x-3">
                  {expandedId === p.id ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
                  <Shield className="w-5 h-5 text-slate-500" />
                  <div>
                    <p className="text-sm font-semibold text-slate-800">{p.rule_name}</p>
                    <p className="text-xs text-slate-500">{p.description}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${typeColors[p.rule_type] || 'bg-slate-100'}`}>{p.rule_type?.toUpperCase()}</span>
                  <span className="text-xs text-slate-500">Eff: {p.effective_from}</span>
                  <span className={`w-2 h-2 rounded-full ${p.is_active ? 'bg-emerald-400' : 'bg-slate-300'}`} />
                  <button data-testid={`edit-param-${p.id}`} onClick={(e) => { e.stopPropagation(); openEdit(p); }} className="text-slate-400 hover:text-[#0052FF]"><Edit className="w-4 h-4" /></button>
                  <button data-testid={`delete-param-${p.id}`} onClick={(e) => { e.stopPropagation(); handleDelete(p.id); }} className="text-slate-400 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
              {expandedId === p.id && (
                <div className="border-t border-slate-100 p-4 bg-slate-50/50">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
                    <div><p className="text-slate-500 mb-1">Wage Basis</p><p className="font-medium text-slate-800 uppercase">{p.wage_basis || '-'}</p></div>
                    <div><p className="text-slate-500 mb-1">State</p><p className="font-medium text-slate-800">{p.state || 'All'}</p></div>
                    <div><p className="text-slate-500 mb-1">Due Day</p><p className="font-medium text-slate-800">{p.due_day || '-'}</p></div>
                    <div><p className="text-slate-500 mb-1">Rounding</p><p className="font-medium text-slate-800">{p.rounding_rule || '-'}</p></div>
                    {p.employee_share?.rate > 0 && <div><p className="text-slate-500 mb-1">Employee Rate</p><p className="font-medium text-slate-800">{p.employee_share.rate}%</p></div>}
                    {p.employer_share?.rate > 0 && <div><p className="text-slate-500 mb-1">Employer Rate</p><p className="font-medium text-slate-800">{p.employer_share.rate}%</p></div>}
                    {p.thresholds?.wage_ceiling && <div><p className="text-slate-500 mb-1">Wage Ceiling</p><p className="font-medium text-slate-800">{p.thresholds.wage_ceiling?.toLocaleString()}</p></div>}
                    {p.thresholds?.eligibility_threshold && <div><p className="text-slate-500 mb-1">Eligibility Threshold</p><p className="font-medium text-slate-800">{p.thresholds.eligibility_threshold?.toLocaleString()}</p></div>}
                  </div>
                  {p.slab_table?.length > 0 && (
                    <div className="mt-4">
                      <p className="text-xs text-slate-500 mb-2 font-medium">Slab Table</p>
                      <table className="w-full text-xs">
                        <thead><tr className="text-slate-500"><th className="text-left py-1 pr-4">From</th><th className="text-left py-1 pr-4">To</th><th className="text-left py-1 pr-4">Rate %</th><th className="text-left py-1">Fixed</th></tr></thead>
                        <tbody>{p.slab_table.map((s, i) => (
                          <tr key={i} className="text-slate-700"><td className="py-1 pr-4">{s.from_amount?.toLocaleString()}</td><td className="py-1 pr-4">{s.to_amount >= 99999999 ? '∞' : s.to_amount?.toLocaleString()}</td><td className="py-1 pr-4">{s.rate || 0}%</td><td className="py-1">{s.fixed_amount || 0}</td></tr>
                        ))}</tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
          {parameters.length === 0 && !loading && (
            <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
              <Shield className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-base font-semibold text-slate-600">No Statutory Rules Configured</h3>
              <p className="text-sm text-slate-400 mt-1 mb-4">Click "Seed Defaults" to load Indian statutory defaults</p>
            </div>
          )}
        </div>

        {showModal && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 overflow-y-auto" onClick={() => setShowModal(false)}>
            <div className="bg-white rounded-xl p-6 w-full max-w-lg shadow-xl my-8" onClick={(e) => e.stopPropagation()}>
              <h3 className="text-lg font-semibold text-slate-900 mb-4">{editParam ? 'Edit Rule' : 'New Statutory Rule'}</h3>
              <form onSubmit={handleSave} className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="block text-sm font-medium text-slate-700 mb-1">Rule Name</label>
                    <input data-testid="rule-name-input" value={form.rule_name} onChange={(e) => setForm({ ...form, rule_name: e.target.value })} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" required /></div>
                  <div><label className="block text-sm font-medium text-slate-700 mb-1">Rule Type</label>
                    <select data-testid="rule-type-select" value={form.rule_type} onChange={(e) => setForm({ ...form, rule_type: e.target.value })} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm">
                      {RULE_TYPES.map((rt) => <option key={rt.value} value={rt.value}>{rt.label}</option>)}
                    </select></div>
                </div>
                <div><label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
                  <input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" /></div>
                <div className="grid grid-cols-3 gap-3">
                  <div><label className="block text-xs font-medium text-slate-700 mb-1">Effective From</label>
                    <input type="date" value={form.effective_from} onChange={(e) => setForm({ ...form, effective_from: e.target.value })} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" required /></div>
                  <div><label className="block text-xs font-medium text-slate-700 mb-1">Effective To</label>
                    <input type="date" value={form.effective_to || ''} onChange={(e) => setForm({ ...form, effective_to: e.target.value || null })} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" /></div>
                  <div><label className="block text-xs font-medium text-slate-700 mb-1">State</label>
                    <input value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} placeholder="e.g. Maharashtra" className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" /></div>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div><label className="block text-xs font-medium text-slate-700 mb-1">Wage Basis</label>
                    <select value={form.wage_basis} onChange={(e) => setForm({ ...form, wage_basis: e.target.value })} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm">
                      <option value="basic">Basic</option><option value="basic_da">Basic + DA</option><option value="gross">Gross</option><option value="ctc">CTC</option>
                    </select></div>
                  <div><label className="block text-xs font-medium text-slate-700 mb-1">Employee Rate %</label>
                    <input type="number" step="0.01" value={form.employee_share?.rate || 0} onChange={(e) => setForm({ ...form, employee_share: { ...form.employee_share, rate: parseFloat(e.target.value) || 0 } })} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" /></div>
                  <div><label className="block text-xs font-medium text-slate-700 mb-1">Employer Rate %</label>
                    <input type="number" step="0.01" value={form.employer_share?.rate || 0} onChange={(e) => setForm({ ...form, employer_share: { ...form.employer_share, rate: parseFloat(e.target.value) || 0 } })} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="block text-xs font-medium text-slate-700 mb-1">Wage Ceiling</label>
                    <input type="number" value={form.thresholds?.wage_ceiling || ''} onChange={(e) => setForm({ ...form, thresholds: { ...form.thresholds, wage_ceiling: parseFloat(e.target.value) || null } })} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" /></div>
                  <div><label className="block text-xs font-medium text-slate-700 mb-1">Due Day of Month</label>
                    <input type="number" min={1} max={31} value={form.due_day} onChange={(e) => setForm({ ...form, due_day: parseInt(e.target.value) || 15 })} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" /></div>
                </div>
                <div className="grid grid-cols-2 gap-3 bg-slate-50 p-3 rounded-lg border border-slate-200">
                  <div>
                    <label className="block text-xs font-semibold text-slate-800 mb-1">Who Pays? (Payment Responsibility)</label>
                    <select
                      data-testid="payment-responsibility-select"
                      value={form.payment_responsibility || 'COMPANY_PAYS'}
                      onChange={(e) => setForm({ ...form, payment_responsibility: e.target.value })}
                      className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white"
                    >
                      <option value="COMPANY_PAYS">Company Pays</option>
                      <option value="CLIENT_COMPANY_PAYS">Client Company Pays</option>
                    </select>
                  </div>
                  {form.payment_responsibility === 'CLIENT_COMPANY_PAYS' && (
                    <div>
                      <label className="block text-xs font-semibold text-slate-800 mb-1">Client Company</label>
                      <select
                        data-testid="client-company-select"
                        value={form.client_company_id || ''}
                        onChange={(e) => setForm({ ...form, client_company_id: e.target.value })}
                        className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white"
                      >
                        <option value="">— Select Client Company —</option>
                        {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                      </select>
                    </div>
                  )}
                </div>
                <div className="flex items-center space-x-4">
                  <label className="flex items-center space-x-2"><input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} /><span className="text-sm text-slate-700">Active</span></label>
                  <label className="flex items-center space-x-2"><input type="checkbox" checked={form.override_permissions?.allow_voluntary_higher || false} onChange={(e) => setForm({ ...form, override_permissions: { ...form.override_permissions, allow_voluntary_higher: e.target.checked } })} /><span className="text-sm text-slate-700">Allow Voluntary Higher</span></label>
                </div>
                <div className="flex justify-end space-x-3 pt-2">
                  <button type="button" onClick={() => setShowModal(false)} className="px-4 py-2 text-sm text-slate-600 border border-slate-300 rounded-lg hover:bg-slate-50">Cancel</button>
                  <button data-testid="save-parameter-btn" type="submit" className="px-4 py-2 text-sm bg-[#0052FF] text-white rounded-lg hover:bg-[#0040CC]">Save</button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default StatutorySetup;
